import { useCallback, useState } from "react";

/**
 * Debounce a function
 * @param executionFunction the function to be debounced
 * @param debounceTime the time to wait before calling the function
 * @returns a debounced function that can be called at any time to call the execution function after the debounce time if not called again before the debounce time
 */
export function useDebounceFunction<T>(
  executionFunction: T extends void ? () => void : (payload: T) => void,
  debounceTime: number
) {
  const [timeout, setTimeoutState] = useState<NodeJS.Timeout | null>(null);

  // Debounced function to be returned
  const debouncedFunction = useCallback(
    (payload?: T) => {
      // Clear the previous timer if there is one
      if (timeout) {
        clearTimeout(timeout);
      }

      // Set a new timer
      setTimeoutState(
        setTimeout(() => {
          executionFunction(payload);
        }, debounceTime)
      );
    },
    [executionFunction, debounceTime]
  );

  return debouncedFunction;
}
